import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';

import { Loader2, Leaf, Info } from 'lucide-react';
import { Link } from 'react-router';
import { Input } from '@/components/ui/input';

// Define the species type
interface Species {
  speciesId: string;
  typeIndex: number;
  commonType: string;
  englishName: string;
  btoEnglishName: string;
  order: string;
  family: string;
  genus: string;
  scientificName: string;
  authority: string;
  breedingRegions: string;
  breedingSubRegions: string;
  nonBreedingRegions: string;
  createdAt: string;
  updatedAt: string;
}

const AdminSpecies: React.FC = () => {

  const [searchTerm, setSearchTerm] = useState<string | null>(null);

  // Fetch species
  const { data: specList, isLoading: isLoading, error: searchError } = useQuery({
    queryKey: ['species', searchTerm],
    queryFn: async () => {
      let url = '/api/species'
      if (searchTerm && searchTerm.length >= 3) {
        url = `/api/species?search=${encodeURIComponent(searchTerm)}`
      }
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch species');
      }
      return response.json() as Promise<Species[]>;
    },
  });

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="w-full max-w-full px-4 md:px-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Species</h1>
      </div>

      <div className="w-full">
        <div className='flex flex-row justify-around w-full mb-4'>
          <Input placeholder='Search Species' value={searchTerm ?? ''} onChange={(x) => setSearchTerm(x.target.value)} />
        </div>
        <div>
          {isLoading && (
            <div className="flex justify-center p-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}
          {searchError && !isLoading && (
            <div className="bg-red-50 p-4 rounded-md text-red-800">
              Failed to load all species. Please try again.
            </div>
          )}
          <div className="space-y-4">
            {!isLoading && specList && specList.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {specList.map((species) => (
                  <Card key={species.speciesId}>
                    <CardHeader>
                      <div className="flex justify-between">
                        <div className="flex items-center">
                          <Leaf className="h-5 w-5 mr-2 text-green-600" />
                          <CardTitle>{species.englishName}</CardTitle>
                        </div>
                        <Button asChild variant="ghost" size="sm">
                          <Link to={`/app/admin/speciesDetail/${species.speciesId}`}>
                            <Info className="h-4 w-4 mr-1" />
                            View Details
                          </Link>
                        </Button>
                      </div>
                      <CardDescription className="italic">{species.scientificName}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        {species.authority && (
                          <div className="col-span-2">
                            <span className="text-sm font-medium">Authority:</span>
                            <p className="text-sm text-muted-foreground">{species.authority}</p>
                          </div>
                        )}

                        <div>
                          <span className="text-sm font-medium">Family:</span>
                          <p className="text-sm text-muted-foreground">{species.family}</p>
                        </div>

                        <div>
                          <span className="text-sm font-medium">Order:</span>
                          <p className="text-sm text-muted-foreground">{species.order}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                No species found
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { AdminSpecies };
